import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { type Theme, alpha } from '@mui/material/styles';

import { BRAND_COLORS, BRAND_DARK_MODE } from '~/components/brand';

/**
 * The askRichard thread's own surface tokens, derived from the Lens theme so the chat
 * reads as one system with the board. Light: a teal spine on Compass White, your turn
 * in Sandy Beige, askRichard's turn in a Compass White bubble with a hairline teal edge.
 * Dark: the same shapes on the brand's teal field. Coral Green appears only as the
 * streaming dot.
 */
export type PaneTokens = {
  /** The vertical thread line and the marker ring on your turn. */
  spine: string;
  /** Fill behind your words. Never grey. */
  userBubble: string;
  /** Fill behind askRichard's words. */
  assistantBubble: string;
  /** Hairline edge on askRichard's bubble, chips and the composer. */
  hairline: string;
  /** Resting rule above the composer; the focused rule is `accent`. */
  composerRule: string;
  /** Structural accent: Ocean Teal in light, Compass White in dark. */
  accent: string;
  accentContrast: string;
  /** The streaming dot. */
  liveDot: string;
};

export const BUBBLE_RADIUS = 8;
export const BUBBLE_CORNER = 8;
/** The marker ring on your turn, centred on the spine. */
export const SPINE_MARKER = 10;
export const SPINE_WIDTH = 2;
/** Where the spine sits inside the thread, measured from the thread's left edge: the avatar's centre. */
export const SPINE_X = 16;
export const USER_BUBBLE_MAX_WIDTH = '86%';

export function paneTokens(theme: Theme): PaneTokens {
  const lens = lensTokens(theme);
  const dark = theme.palette.mode === 'dark';
  return {
    spine: alpha(lens.accent, dark ? 0.32 : 0.24),
    userBubble: dark ? BRAND_DARK_MODE.card : BRAND_COLORS.sandyBeige,
    assistantBubble: dark ? BRAND_DARK_MODE.card : BRAND_COLORS.compassWhite,
    hairline: dark ? BRAND_DARK_MODE.cardRule : alpha(BRAND_COLORS.oceanTeal, 0.12),
    composerRule: dark ? BRAND_DARK_MODE.cardRule : alpha(BRAND_COLORS.jetBlack, 0.14),
    accent: lens.accent,
    accentContrast: lens.accentContrast,
    liveDot: lens.coralAccent,
  };
}
